import { useState, useEffect, useRef } from 'react';
import { LoginModal } from '../../features/auth/components/LoginModal.jsx';
import { AIDisclaimerModal } from '../../features/auth/components/AIDisclaimerModal.jsx';
import { Button } from '../../shared/components/Button.jsx';
import {
  DUALITY_FEAR_COLOR,
  DUALITY_HOPE_COLOR,
  parseDiceCommand,
} from '../../features/orchestrator/utils/diceCommands';
import { getDiceBox } from '../../shared/utils/diceBox';

import '../../shared/styles/Login.css';
import '../../styles/Dice.css';
import { Airplay, Bot } from 'lucide-react';

export default function Login() {
  const [txt1, setTxt1] = useState('D');
  const [txt2, setTxt2] = useState('F');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAIDisclaimer, setShowAIDisclaimer] = useState(false);
  const [system, setSystem] = useState('trialment');
  const diceBoxRef = useRef(null);
  const pendingDualityRollRef = useRef(false);

  useEffect(() => {
    let active = true;
    getDiceBox().then((diceBox) => {
      if (!active) return;
      diceBoxRef.current = diceBox;
      if (pendingDualityRollRef.current) {
        pendingDualityRollRef.current = false;
        const duality = parseDiceCommand('Duality standard');
        diceBox.roll([
          { qty: 1, sides: duality.hopeSides, themeColor: DUALITY_HOPE_COLOR },
          { qty: 1, sides: duality.fearSides, themeColor: DUALITY_FEAR_COLOR },
        ]);
      }
    }).catch((error) => {
      console.error('Failed to initialize login dice box:', error);
    });

    return () => {
      active = false;
      pendingDualityRollRef.current = false;
      diceBoxRef.current = null;
    };
  }, []);

  //which expands into
  const word1 = 'Dimension';
  const word2 = 'Factory';
  const timeoutValue = 30;
  const loadingTimeout = timeoutValue * (word1.length + word2.length);
  //with the use of
  const expand = () => {
    let i = 1;
    const expandTxt1 = () => {
      if (i <= word1.length) {
        setTxt1(word1.substring(0, i));
        i++;
        setTimeout(expandTxt1, timeoutValue);
      }
    };
    let j = 1;
    const expandTxt2 = () => {
      if (j <= word2.length) {
        setTxt2(word2.substring(0, j));
        j++;
        setTimeout(expandTxt2, timeoutValue);
      }
    };
    expandTxt1();
    setTimeout(expandTxt2, timeoutValue * word1.length);
  };
  const contract = () => {
    let i = Math.max(word1.length);
    const contractTxt1 = () => {
      if (i > 0) {
        setTxt1(word1.substring(0, i));
        i--;
        setTimeout(contractTxt1, timeoutValue);
      }
    };
    let j = Math.max(word2.length);
    const contractTxt2 = () => {
      if (j > 0) {
        setTxt2(word2.substring(0, j));
        j--;
        setTimeout(contractTxt2, timeoutValue);
      }
    };
    contractTxt2();
    setTimeout(contractTxt1, timeoutValue * word2.length);
  };
  //at the Start
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      expand();
      setTimeout(() => setLoading(false), loadingTimeout);
    }, 200);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const tag = document.querySelector('meta[name="theme-color"]');
    if (tag) tag.setAttribute('content', '#e94f37');
  }, []);
  //or when I click the Logo
  const animate = () => {
    if (!loading) {
      setLoading(true);
      setTimeout(() => setLoading(false), loadingTimeout);
      if (txt1 === 'D') {
        expand();
      } else {
        contract();
      }
    }
  };

  const openModal = (system) => {
    clearDice();
    setSystem(system);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const openAIDisclaimer = () => {
    clearDice();
    setShowAIDisclaimer(true);
  };

  const clearDice = () => {
    pendingDualityRollRef.current = false;
    try {
      diceBoxRef.current?.clear?.();
    } catch (error) {
      console.warn('Login dice box clear warning:', error);
    }
  };

  const rollDuality = () => {
    const diceBox = diceBoxRef.current;
    if (!diceBox) {
      pendingDualityRollRef.current = true;
      return;
    }

    const duality = parseDiceCommand('Duality standard');
    try {
      diceBox.roll([
        { qty: 1, sides: duality.hopeSides, themeColor: DUALITY_HOPE_COLOR },
        { qty: 1, sides: duality.fearSides, themeColor: DUALITY_FEAR_COLOR },
      ]);
    } catch (error) {
      console.warn('Login duality roll failed:', error);
    }
  };

  const closeAIDisclaimer = () => {
    setShowAIDisclaimer(false);
  };

  return (
    <div className='login_page_container'>
      <div className='logo_wrapper' onClick={() => animate()}>
        <h2 className='logo rotate'>{txt1}</h2>
        <h2 className='logo '>{txt2}</h2>
      </div>
      <div style={{ margin: '10px 0 10px 0' }}></div>
      <div className='text'>Home of</div>
      <div style={{ margin: '10px 0 10px 0' }}></div>
      <div>
        <span>
          <Button
            label={'CotA'}
            icon={'cota'}
            type='black-white'
            onPress={() => openModal('cota')}
          />
        </span>
        <span className='button_margin'>
          <Button
            label={'Orchestrator'}
            icon={'orchestrator'}
            type='black-white'
            onPress={() => openModal('orchestrator')}
          />
        </span>
        <span className='button_margin'>
          <Button
            icon={'orchestrator'}
            description={'Orchestrator for Players'}
            type='cinnabar'
            onPress={() => openModal('orchestrator_user')}
          />
        </span>
        <span className='button_margin'>
          <Button
            icon={'trialment'}
            description={'What happened to Trialment?'}
            type='onyx'
            onPress={() => openModal('trialment')}
          />
        </span>
        <span className='button_margin'>
          <Button
            icon={Bot}
            description={'Our Stance on AI'}
            type='onyx'
            onPress={openAIDisclaimer}
          />
        </span>
      </div>
      <LoginModal
        show={showModal}
        system={system}
        close={closeModal}
        onRollDuality={rollDuality}
      />
      <AIDisclaimerModal show={showAIDisclaimer} close={closeAIDisclaimer} />
    </div>
  );
}
